import { VdsButton, VdsNotification } from '@volt/volt-extended-components-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@customer/store/hooks';
import { closeSidePanel } from '@customer/store/slices/globalSlice';
import { DrawerForm, DrawerPanel, SuccessNotificationContext } from '@dmc/ui';
import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import styles from './AddNotePanel.module.scss';
import { PostNoteModel } from '@customer/store/apis/commonModels';

const defaultValues: PostNoteModel = {
  text: '',
};

const MinCharacters = 1;
const MaxCharacters = 2000;

export interface AddNotePanelProps {
  successTrans: string;
  errorTrans: string;
  onSubmit: (text: string) => Promise<boolean>;
}

export function AddNotePanel({ successTrans, errorTrans, onSubmit }: AddNotePanelProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { showSuccessMessage } = useContext(SuccessNotificationContext);

  const schema = useMemo(() => {
    return z.object({
      text: z
        .string()
        .min(MinCharacters, {
          message: t('validation.minCharacters', { count: MinCharacters }),
        })
        .max(MaxCharacters, {
          message: t('validation.maxCharacters', { count: MaxCharacters }),
        }),
    });
  }, [t]);

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isDirty, isValid },
  } = useForm<PostNoteModel>({
    defaultValues,
    resolver: zodResolver(schema),
    mode: 'all',
    reValidateMode: 'onBlur',
  });

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const canSubmit = !isSubmitting && isDirty && isValid;

  const onSubmitInternal = useCallback(
    async ({ text }: PostNoteModel): Promise<void> => {
      const success = await onSubmit(text);
      if (success) {
        // Success: Clear any error message, show the success toast, and close the panel.
        setErrorMessage(undefined);
        showSuccessMessage(t(successTrans));
        dispatch(closeSidePanel());
      } else {
        setErrorMessage(t(errorTrans));
      }
    },
    [onSubmit, showSuccessMessage, t, successTrans, dispatch, errorTrans],
  );

  return (
    <DrawerPanel
      header={t('AddNote')}
      footer={
        <VdsButton
          variant="primary"
          size="medium"
          disabled={!canSubmit}
          isFullWidth
          onClick={() => handleSubmit(onSubmitInternal)()}
        >
          {t('Submit')}
        </VdsButton>
      }
    >
      <DrawerForm
        children={
          <>
            {typeof errorMessage !== 'undefined' && (
              <div className={styles.field}>
                <VdsNotification variant="toast" status="danger" showCloseButton={false}>
                  <div slot="header">{t('Error')}</div>
                  <span slot="body">{errorMessage}</span>
                </VdsNotification>
              </div>
            )}

            <div className={styles.field}>
              <CasFormInput control={control} name="text" label={t('Text')} element="textarea" />
            </div>
          </>
        }
      />
    </DrawerPanel>
  );
}

export default AddNotePanel;
